import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import CustomImage from '../TipTapEditor/TipTapImageExtension';
import '../TipTapEditor/TipTapEditor.css';

const TipTapPreguntaEditor = ({ 
  value = '', 
  handleChange, 
  placeholder = 'Escriba el texto de la pregunta...',
  expandedHeight = '300px'
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const fileInputRef = useRef(null);

  // Initialize editor with extensions
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        history: {
          depth: 100,
        },
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: ['left', 'center', 'right', 'justify'],
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'tiptap-link',
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      Placeholder.configure({
        placeholder: placeholder,
        emptyEditorClass: 'is-editor-empty',
      }),
      CustomImage,
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      if (handleChange) {
        handleChange(html);
      }
    },
    editorProps: {
      attributes: {
        class: 'tiptap-editor-content',
        spellcheck: 'false',
      },
      handleClick: (view) => {
        if (!view.hasFocus()) {
          view.focus();
        }
        return false;
      },
      handleDrop: (view, event, slice, moved) => {
        if (!moved && event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]) {
          const file = event.dataTransfer.files[0];
          if (file.type.startsWith('image/')) {
            event.preventDefault();
            handleImageUpload(file);
            return true;
          }
        }
        return false;
      },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (items) {
          for (let i = 0; i < items.length; i++) {
            if (items[i].type.startsWith('image/')) {
              const file = items[i].getAsFile();
              if (file) {
                event.preventDefault();
                handleImageUpload(file);
                return true;
              }
            }
          }
        }
        return false;
      },
    },
    autofocus: false,
  }, []);

  // Update editor content when value changes from outside
  useEffect(() => {
    if (editor && value !== editor.getHTML() && !isInitialized) {
      editor.commands.setContent(value || '');
      setIsInitialized(true);
    }
  }, [editor, value, isInitialized]);

  // Handle image upload (convert to base64)
  const handleImageUpload = useCallback((file) => {
    if (!editor) return;

    setIsUploading(true);
    const reader = new FileReader();

    reader.onload = (e) => {
      const src = e.target.result;
      
      const img = new Image();
      img.onload = () => {
        const maxWidth = 600;
        const ratio = img.width / img.height;
        const width = Math.min(img.width, maxWidth);
        const height = width / ratio;

        editor.chain().focus().setImage({
          src,
          alt: file.name,
          width: Math.round(width),
          height: Math.round(height),
          alignment: 'center',
        }).run();

        setIsUploading(false);
      };
      img.src = src;
    };

    reader.onerror = () => {
      console.error('Error al leer el archivo de imagen');
      setIsUploading(false);
    };

    reader.readAsDataURL(file);
  }, [editor]);

  const handleImageButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handleImageUpload(file);
    }
  };

  const handleEditorContainerMouseDown = (event) => {
    if (!editor) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    // Si el clic es en el espacio del contenedor (fuera de ProseMirror), enfocar inmediatamente.
    if (!target.closest('.ProseMirror')) {
      editor.chain().focus().run();
    }
  };

  const ToolbarButton = ({ onClick, isActive, disabled, children, title }) => (
    <button
      onMouseDown={(e) => { e.preventDefault(); if (!disabled) onClick(e); }}
      disabled={disabled}
      className={`tiptap-toolbar-btn ${isActive ? 'is-active' : ''}`}
      title={title}
      type="button"
    >
      {children}
    </button>
  );

  const isImageSelected = editor?.isActive('customImage');

  const renderImageControls = () => {
    if (!isImageSelected) return null;

    return (
      <>
        <div className="tiptap-toolbar-divider" />
        <div className="tiptap-toolbar-group tiptap-image-controls">
          <span className="tiptap-toolbar-label">Imagen:</span>
          <button
            onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().updateImageAlignment('left').run(); }}
            className="tiptap-toolbar-btn"
            title="Alinear imagen a la izquierda"
            type="button"
          >
            ⬅
          </button>
          <button
            onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().updateImageAlignment('center').run(); }}
            className="tiptap-toolbar-btn"
            title="Centrar imagen"
            type="button"
          >
            ⬇
          </button>
          <button
            onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().updateImageAlignment('right').run(); }}
            className="tiptap-toolbar-btn"
            title="Alinear imagen a la derecha"
            type="button"
          >
            ➡
          </button>
          <button
            onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().deleteSelection().run(); }}
            className="tiptap-toolbar-btn tiptap-btn-danger"
            title="Eliminar imagen"
            type="button"
          >
            🗑️
          </button>
        </div>
      </>
    );
  };

  if (!editor) {
    return <div className="tiptap-loading">Cargando editor...</div>;
  }

  return (
    <div className="tiptap-pregunta-editor-wrapper">
      <div className="tiptap-editor-wrapper compact">
        {/* Toolbar compacto */}
        <div className="tiptap-toolbar">
          {/* Formato de texto básico */}
          <div className="tiptap-toolbar-group">
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleBold().run()}
              isActive={editor.isActive('bold')}
              title="Negrita (Ctrl+B)"
            >
              <strong>B</strong>
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleItalic().run()}
              isActive={editor.isActive('italic')}
              title="Cursiva (Ctrl+I)"
            >
              <em>I</em>
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              isActive={editor.isActive('underline')}
              title="Subrayado (Ctrl+U)"
            >
              <u>U</u>
            </ToolbarButton>
          </div>

          <div className="tiptap-toolbar-divider" />

          {/* Encabezados */}
          <div className="tiptap-toolbar-group">
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              isActive={editor.isActive('heading', { level: 2 })}
              title="Título 2"
            >
              H2
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              isActive={editor.isActive('heading', { level: 3 })}
              title="Título 3"
            >
              H3
            </ToolbarButton>
          </div>

          <div className="tiptap-toolbar-divider" />

          {/* Listas */}
          <div className="tiptap-toolbar-group">
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              isActive={editor.isActive('bulletList')}
              title="Lista con viñetas"
            >
              •
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              isActive={editor.isActive('orderedList')}
              title="Lista numerada"
            >
              1.
            </ToolbarButton>
          </div>

          <div className="tiptap-toolbar-divider" />

          {/* Alineación */}
          <div className="tiptap-toolbar-group">
            <ToolbarButton
              onClick={() => editor.chain().focus().setTextAlign('left').run()}
              isActive={editor.isActive({ textAlign: 'left' })}
              title="Alinear izquierda"
            >
              ⬅
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().setTextAlign('center').run()}
              isActive={editor.isActive({ textAlign: 'center' })}
              title="Centrar"
            >
              ⬇
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().setTextAlign('right').run()}
              isActive={editor.isActive({ textAlign: 'right' })}
              title="Alinear derecha"
            >
              ➡
            </ToolbarButton>
          </div>

          <div className="tiptap-toolbar-divider" />

          {/* Imagen */}
          <div className="tiptap-toolbar-group">
            <ToolbarButton
              onClick={handleImageButtonClick}
              disabled={isUploading}
              title="Insertar imagen (Arrastra o pega)"
            >
              {isUploading ? '⏳' : '🖼️'}
            </ToolbarButton>
          </div>

          <div className="tiptap-toolbar-divider" />

          {/* Undo/Redo */}
          <div className="tiptap-toolbar-group">
            <ToolbarButton
              onClick={() => editor.chain().focus().undo().run()}
              disabled={!editor.can().undo()}
              title="Deshacer (Ctrl+Z)"
            >
              ↶
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().redo().run()}
              disabled={!editor.can().redo()}
              title="Rehacer (Ctrl+Y)"
            >
              ↷
            </ToolbarButton>
          </div>

          {/* Controles de imagen (cuando está seleccionada) */}
          {renderImageControls()}
        </div>

        {/* Área de contenido del editor */}
        <div 
          className="tiptap-editor-container" 
          onMouseDown={handleEditorContainerMouseDown}
          style={{ 
            minHeight: expandedHeight,
            maxHeight: expandedHeight 
          }}
        >
          <EditorContent editor={editor} />
        </div>

        {/* Input oculto para subir imágenes */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />
      </div>
    </div>
  );
};

export default TipTapPreguntaEditor;
